import { camelCase } from 'lodash-es';
import { useTranslation } from 'next-i18next/pages';

import { EmploymentType } from '@pflegia/types/api/candidate/employment-type';
import { JobPosition } from '@pflegia/types/api/job-position/job-position';

import { employerTypeTitle } from '@components/position-card/employer-type-mappings';

import { useParseJobPositionForPerks } from '@hooks/get-job-position.hook';

import { careTypeToTypeOfEmployer } from '@shared/application/care-type-to-type-of-employer';
import { normalizeTextForJsonLd } from '@shared/utils/normalize-text-for-json-ld';
import { toPlainText } from '@shared/utils/to-plain-text';

export const useGetJsonldJobpositionDescriptionOutput = (jobPosition: JobPosition) => {
  const { t } = useTranslation(['profile', 'candidateFields']);
  const {
    employer,
    vacationDays,
    benefits,
    candidateProfile,
    responsibilities,
    departments,
    contractTypes,
    geoLocation,
    onboardingProcedure,
    employmentTypes,
    shiftDurationHours,
    careShare,
    canStartNow,
    canStartDate,
    salaryLow,
    salaryHigh,
  } = jobPosition;
  const { perks } = useParseJobPositionForPerks(jobPosition);

  const getDepartment = (name: string) => t(`candidateFields:departments.${camelCase(name)}`);

  const hasDepartments = departments && departments?.length > 0;
  const hasVacationDays = vacationDays && vacationDays !== '0';
  const hasBenefits = benefits && benefits?.length > 0;
  const hasCandidateProfile = candidateProfile && candidateProfile?.length > 0;
  const hasResponsibilities = responsibilities && responsibilities?.length > 0;
  const hasContractTypes = contractTypes && contractTypes?.length > 0;
  const hasOvertimeCompensations = employer.overtimeCompensations && employer.overtimeCompensations?.length > 0;
  const hasSalaryNegotiations = employer.salaryNegotiations && employer.salaryNegotiations?.length > 0;
  const hasEmploymentTypes = employmentTypes && employmentTypes?.length > 0;

  const vacationsText = hasVacationDays ? `Urlaubstage pro Jahr: ${vacationDays}` : '';
  const salaryText =
    jobPosition.showSalary && employer.showSalary && salaryLow && salaryHigh
      ? `Dein geschätztes Gehalt: ${salaryLow} - ${salaryHigh} €`
      : '';
  const numberOfBedsText = perks.numberOfBeds ? `Anzahl der Betten: ${perks.numberOfBeds}` : '';
  const employmentTypesText = hasEmploymentTypes
    ? `Beschäftigungsverhältnis: ${employmentTypes
        ?.map(type => {
          if (type === EmploymentType.FullTime) return 'Vollzeit';
          if (type === EmploymentType.PartTime) return 'Teilzeit';
          if (type === EmploymentType.Marginal) return 'Geringfügig';
          if (type === EmploymentType.Apprenticeship) return 'Ausbildung';
          return '';
        })
        .filter(Boolean)
        .join(', ')}`
    : '';
  const contractTypesText = hasContractTypes ? `Vertragstyp: ${contractTypes?.map(type => type.title).join(', ')}` : '';
  const overtimeCompensationsText = hasOvertimeCompensations
    ? `Überstundenregelung: ${employer?.overtimeCompensations?.map(type => type.title).join(', ')}`
    : '';
  const salaryNegotiationsText = hasSalaryNegotiations
    ? `Gehaltsverhandlungen: ${employer?.salaryNegotiations?.map(type => type.title).join(', ')}`
    : '';
  const canStartNowText = canStartNow || canStartDate ? `Arbeitsbeginn: ${canStartNow ? 'Sofort' : canStartDate}` : '';

  const teamSize = jobPosition.teamSize || employer.teamSize;
  const teamSizeText = teamSize ? `Teamgröße: ${teamSize}` : '';

  const careShareText = careShare ? `Pflegeanteil: ${careShare}` : '';
  const carForPrivateUseText = perks.carForPrivateUse ? `Auto für den privaten Gebrauch: ${perks.carForPrivateUse}` : '';
  const shiftDurationHoursText = shiftDurationHours ? `Arbeitszeit: ${shiftDurationHours}` : '';
  const zipCodeText = geoLocation.postalCode ? `Postleitzahl: ${geoLocation.postalCode}` : '';

  const departmentsText = hasDepartments
    ? `Einrichtungstyp: ${[
        t(`candidateFields:typeOfEmployer.${employerTypeTitle(careTypeToTypeOfEmployer(employer?.careType))}`),
        ...(departments?.map(department => getDepartment(department.name)) ?? []),
      ].join(', ')}`
    : '';
  const benefitsText = hasBenefits ? `Das bieten wir: ${benefits?.map(benefit => benefit.title).join(', ')}` : '';
  const candidateProfileText = hasCandidateProfile
    ? `Profil des Bewerbers: ${candidateProfile?.map(item => toPlainText(item)).join(', ')}`
    : '';
  const responsibilitiesText = hasResponsibilities
    ? `Zuständigkeiten: ${responsibilities?.map(responsibility => toPlainText(responsibility)).join(', ')}`
    : '';
  const aboutUsText = employer.aboutUs ? `Über uns: ${toPlainText(employer.aboutUs)}` : '';
  const onBoardingProcedureText = onboardingProcedure ? `Einarbeitung: ${toPlainText(onboardingProcedure)}` : '';
  const bestArgumentText = employer.bestArgumentContent ? `Unser bestes Argument: ${toPlainText(employer.bestArgumentContent)}` : '';

  const description = [
    departmentsText,
    vacationsText,
    salaryText,
    numberOfBedsText,
    employmentTypesText,
    contractTypesText,
    overtimeCompensationsText,
    salaryNegotiationsText,
    canStartNowText,
    teamSizeText,
    careShareText,
    carForPrivateUseText,
    shiftDurationHoursText,
    zipCodeText,
    aboutUsText,
    candidateProfileText,
    responsibilitiesText,
    benefitsText,
    onBoardingProcedureText,
    bestArgumentText,
  ]
    .filter(Boolean)
    .join('\n');

  return normalizeTextForJsonLd(description);
};
